import type Link from 'next/link';
import {
  type ChangeEvent,
  type ComponentType,
  type CSSProperties,
  type KeyboardEvent,
  type KeyboardEventHandler,
  type MouseEvent,
  type MouseEventHandler,
  type ReactNode,
  type RefAttributes,
  type RefCallback,
  type RefObject,
  type TransitionEventHandler,
} from 'react';

import { type DraggableStateSnapshot } from '@hello-pangea/dnd';

import type * as model from './model';

import { type SxPropsCallback } from '../../../types/mui/sx';
import { type QuantitativeSize } from '../../../types/sizes';
import { type ActionsProp } from '../../actions/types';

/**
 * Represents the component that is used by the {@link List} to render the outer container of the
 * items in the list. Under React 19 the ref is passed as a normal prop, so this is any component
 * whose props conform to {@link CustomListItemComponentProps} (which carries the optional `ref`).
 *
 * This component is optionally provided to the {@link List} as a prop.
 *
 * @deprecated
 * This will soon be removed and replaced with dynamic render props, as such, it should not be
 * heavily relied on for future development.
 *
 * @template T
 *   The model of the items rendered in the list.
 */
export type CustomListItemComponent<T extends model.ListModel> = ComponentType<
  CustomListItemComponentProps<T> & RefAttributes<HTMLDivElement>
>;

/**
 * Represents the minimum form of the props that the component,
 * {@link CustomListItemComponent}, that is provided to the {@link List} via the
 * 'itemComponent' prop, must accept.  In other words, this type defines the form that any component
 * that is provided to the {@link List} via the 'itemComponent' prop must conform to.
 *
 * @deprecated
 * This will soon be removed and replaced with dynamic render props, as such, it should not be
 * heavily relied on for future development.
 */
export type CustomListItemComponentProps<T extends model.ListModel> = Partial<{
  readonly children: ReactNode;
  readonly 'data-rfd-draggable-context-id': string;
  readonly 'data-rfd-draggable-id': string;
  readonly 'data-testid': string;
  readonly id: string;
  readonly item: T;
  readonly onClick: MouseEventHandler<HTMLDivElement>;
  readonly onKeyDown: KeyboardEventHandler<HTMLDivElement>;
  readonly onMouseEnter: MouseEventHandler<HTMLDivElement>;
  readonly onMouseLeave: MouseEventHandler<HTMLDivElement>;
  readonly onTransitionEnd: TransitionEventHandler;
  readonly ref?: RefCallback<HTMLDivElement> | RefObject<HTMLDivElement>;
  readonly style: CSSProperties;
  readonly sx: SxPropsCallback;
  readonly tabIndex: number;
}>;

export type ExtendedCustomListItemComponentProps<T extends model.ListModel> = {
  readonly isLocked?: boolean;
} & CustomListItemComponentProps<T>;

export type ListItemComponent = 'a' | 'div' | 'li' | typeof Link;

export const ListItemDefaultComponent = 'li' as const satisfies ListItemComponent;

export type ListItemDefaultComponent = typeof ListItemDefaultComponent;

export type ListItemElement = HTMLAnchorElement | HTMLDivElement | HTMLLIElement;

export type ListItemKeyboardEvent = KeyboardEvent<ListItemElement>;

export type ListItemMouseEvent = MouseEvent<ListItemElement>;

export type ListItemChangeEvent = ChangeEvent<ListItemElement>;

export type ListItemClickEvent = ListItemKeyboardEvent | ListItemMouseEvent;

export type ListItemSelectEvent = ListItemChangeEvent | ListItemClickEvent;

/**
 * Defines the various ways that an item in a {@link List} or {@link MenuList}
 * can communicate it's state, {@link ListItemState}, to the user via CSS properties or behavior.
 *
 * 1. FontWeight: The font weight of the selected item will be changed to a different weight,
 *                usually a larger font weight (i.e. bolder).
 * 2. Checkbox:   A checkbox will be rendered next to the item, indicating it's state.  This is only
 *                applicable for the {@link ListItemState.Selected} state.
 * 3. Background: The selected item will be highlighted with a background color.
 * 4. TextColor:  The text of the item will be emphasized with a different color.
 * 5. Border:     The border of the item will be highlighted with a different color.
 */
export const ListItemIndicatorMode = Object.freeze({
  Background: 'background',
  Border: 'border',
  Checkbox: 'checkbox',
  FontWeight: 'font-weight',
  TextColor: 'text-color',
} as const);

export type ListItemIndicatorMode =
  (typeof ListItemIndicatorMode)[keyof typeof ListItemIndicatorMode];

export enum ListItemState {
  Focused = 'focused',
  Hovered = 'hovered',
  Selected = 'selected',
}

export type ListItemEffectMode = Extract<
  ListItemIndicatorMode,
  typeof ListItemIndicatorMode.Background | typeof ListItemIndicatorMode.Border
>;

export type AllowedListItemIndicatorMode<S extends ListItemState> = {
  readonly [ListItemState.Focused]: ListItemEffectMode;
  readonly [ListItemState.Hovered]: ListItemEffectMode;
  readonly [ListItemState.Selected]: ListItemIndicatorMode;
}[S];

export const DefaultListItemIndicators: {
  [key in ListItemState]: AllowedListItemIndicatorMode<key>[];
} = {
  [ListItemState.Focused]: [ListItemIndicatorMode.Border],
  [ListItemState.Hovered]: [ListItemIndicatorMode.Background],
  [ListItemState.Selected]: [ListItemIndicatorMode.Checkbox],
};

export interface ListItemCallbackParams extends DraggableStateSnapshot {
  readonly index: number;
  readonly isHovered: boolean;
  readonly isSelected: boolean;
}

export const DefaultDraggableStateSnapshot: DraggableStateSnapshot = {
  combineTargetFor: null,
  combineWith: null,
  draggingOver: null,
  dropAnimation: null,
  isClone: false,
  isDragging: false,
  isDropAnimating: false,
  mode: null,
};

/**
 * Represents the general form of a callback function that can be used to configure properties or
 * behavior of an individual item, {@link T}, in the items rendered in a list.
 */
export type ListItemCallback<R, T extends model.ListModel, Params = ListItemCallbackParams> = (
  item: model.ProcessedListItemModel<T>,
  params: Params,
) => R;

export type MenuItemProps<T extends model.ListModel<C>, C extends ListItemComponent> = {
  readonly actions?: ActionsProp;
  readonly autoFocus?: boolean;
  readonly children?: (item: model.ProcessedListItemModel<T>) => ReactNode;
  readonly index: number;
  readonly isDisabled?: boolean;
  readonly isLocked?: boolean;
  readonly item: model.SelectableProcessedListModel<T>;
  readonly nestedIndentation?: QuantitativeSize<'px' | 'rem'>;
  readonly nestedLevel?: number;
  readonly tabIndex?: number;
};

export type CommonMenuItemRenderProps<
  T extends model.ListModel<C>,
  C extends ListItemComponent,
> = Pick<
  MenuItemProps<T, C>,
  | 'autoFocus'
  | 'children'
  | 'isDisabled'
  | 'isLocked'
  | 'nestedIndentation'
  | 'nestedLevel'
  | 'tabIndex'
>;

export type ItemComponentProps<T extends model.ListModel<C>, C extends ListItemComponent> = {
  readonly index: number;
  readonly item: model.AnyProcessedListModel<T>;
  readonly nestedLevel?: number;
} & CommonMenuItemRenderProps<T, C>;

export type ItemComponent<
  T extends model.ListModel<C>,
  C extends ListItemComponent,
> = ComponentType<ItemComponentProps<T, C>>;
